// The app's URL, as a pure function of where the operator is.
//
// Every screen has a path, so a refresh — a tablet waking up, a service worker
// update, a stray pull-to-refresh — comes back to the screen that was showing
// rather than to home. Nothing that is not a screen gets a path: sheets are a
// layer over one, and a half-typed alert form or an open camera is not somewhere
// to come back to.
//
// One sheet is the exception, and it is the andon detail: `/alert/<id>` names
// the andon open over the Andon tab. A push notification has to be able to
// open the app AT the andon it is about, and the only thing a service worker
// can open is a URL. The Andon app gave alerts an address for exactly that
// reason, and the same address is what a handover note can carry.
//
// Kept free of the DOM so the mapping can be tested on its own; the history
// stack itself is `hooks/useRouteSync`.

import type { LeaderView, Screen, Tab } from '@/store/slices/ui';

/** The part of the UI state a URL carries. */
export interface Route {
  leaderView: LeaderView;
  tab: Tab;
  /** Only the screens a URL can name — 'login' and 'ach' are not places. */
  screen: 'home' | 'job' | 'complete' | 'isw' | 'raise';
  /** The work order the job screen is showing, when it is showing one. */
  woId: number | null;
  /**
   * The ISW whose card is open, as its full register id ("ISW-029").
   *
   * In the URL it is only the suffix — /isw/029 — because that is what the
   * floor says out loud and what an operator would type. Null is the library
   * list itself.
   */
  iswId: string | null;
  /**
   * The andon open over the Andon tab, when one is. The alert wins over the
   * tab in the URL, because while it is open the andon IS where you are.
   */
  andonId: number | null;
}

/**
 * Path segments under /isw that belong to the SERVER, not to the SPA.
 *
 * /isw/cards/<file> and /isw/videos/<path> are served by the BFF and never
 * reach this router in a browser — but a hand-typed or stale /isw/cards would,
 * and reading it as an ISW called "CARDS" is a worse answer than the library.
 */
const ISW_RESERVED = new Set(['cards', 'videos']);

/** "029" → "ISW-029". Null for anything that is not a plausible suffix. */
export function iswIdFromPath(segment: string | undefined): string | null {
  if (!segment || ISW_RESERVED.has(segment)) return null;
  return /^[a-z0-9]{1,8}$/.test(segment) ? `ISW-${segment.toUpperCase()}` : null;
}

/** "ISW-029" → "029", for the URL. */
export function iswIdToPath(id: string): string {
  return id.replace(/^ISW-/i, '').toLowerCase();
}

/**
 * A record id out of a path segment: a positive integer and nothing else.
 *
 * The same rule for a work order and an andon. An id that reaches a sheet and
 * matches no record is an empty sheet with no way out, which on a phone reads
 * as the app being broken rather than the link being wrong — so "/job/abc",
 * "/alert/0" and "/alert/1.5" are not ids, and the URL answers as somewhere
 * sensible instead.
 */
function recordId(segment: string | undefined): number | null {
  if (!segment || !/^\d+$/.test(segment)) return null;
  const id = Number(segment);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/** Where a Back from anywhere lands: the floor for a leader, home for anyone else. */
export const homeRoute = (isLeader: boolean): Route => ({
  leaderView: isLeader ? 'floor' : 'station',
  tab: 'home',
  screen: 'home',
  woId: null,
  iswId: null,
  andonId: null,
});

/** The screen part of a URL, for state that may still be at 'login'. */
export const screenOf = (screen: Screen): Route['screen'] =>
  screen === 'job' || screen === 'complete' || screen === 'isw' || screen === 'raise' ? screen : 'home';

const isTabPath = (s: string | undefined): s is Exclude<Tab, 'home'> =>
  s === 'pods' || s === 'alerts' || s === 'andon' || s === 'more';

export function parseRoute(pathname: string): Route {
  const parts = pathname
    .split('/')
    .filter(Boolean)
    .map((s) => s.toLowerCase());
  const home: Route = { leaderView: 'station', tab: 'home', screen: 'home', woId: null, iswId: null, andonId: null };

  if (parts[0] === 'floor') {
    // A leader on the floor has no pod of their own, so no job screen either —
    // only the places their tab bar goes. Pods is the queue at the station
    // they were last at, which is a place even when they are not standing there.
    const tab = parts[1];
    return { ...home, leaderView: 'floor', tab: isTabPath(tab) ? tab : 'home' };
  }

  // The library is a place: an operator reading a card at a cell who loses the
  // tablet to a reload should come back to it, not to home.
  //
  // '/isw' rather than a spelt-out path because that is what the floor calls
  // them. It sits above the BFF's own /isw/cards and /isw/videos without
  // colliding: this matches a single segment, and those are two deep.
  //   /isw      the library list
  //   /isw/029  that ISW's card, so a reload at a cell comes back to the job
  //             the operator was reading rather than to the list.
  if (parts[0] === 'isw' && parts.length <= 2) {
    const iswId = parts.length === 2 ? iswIdFromPath(parts[1]) : null;
    // An unreadable suffix answers as the library rather than 404ing: the list
    // is one tap from the card, and a dead end is not.
    return { ...home, tab: 'more', screen: 'isw', iswId };
  }

  if (parts[0] === 'job') {
    // Only a URL that actually names a work order is a job URL. Anything else
    // ("/job", "/job/nonsense") is a typo or a stale link, and home is a better
    // answer than a job screen with no job in it.
    const woId = recordId(parts[1]);
    return woId != null ? { ...home, screen: 'job', woId } : home;
  }

  // One andon, open over the Andon tab — the address a push notification
  // opens, and the one a handover note can carry. An unreadable id answers as
  // home, by the same reasoning as a job: the Andon tab is one tap from home,
  // and an empty sheet is not.
  if (parts[0] === 'alert') {
    const andonId = recordId(parts[1]);
    return andonId != null && parts.length === 2 ? { ...home, tab: 'andon', andonId } : home;
  }

  // The raise is a place with a clean first step. The draft is deliberately
  // not in the URL: a half-finished raise is not somewhere to be sent, and
  // the words somebody dictated are not going in an address bar.
  if (parts.length === 1 && parts[0] === 'raise') return { ...home, screen: 'raise' };

  // The completion screen is an acknowledgement of something that has just
  // happened, not a place. Landing on its URL cold has nothing to acknowledge,
  // so it answers as home.
  if (parts.length === 1 && isTabPath(parts[0])) return { ...home, tab: parts[0] };

  return home;
}

export function routePath(route: Route): string {
  // The alert wins over everything, and that is the model showing through
  // rather than a special case: it opens OVER whichever tab a person was on,
  // so while it is open it is where they are — on the floor or at a station.
  if (route.andonId != null) return `/alert/${route.andonId}`;
  // The raise is reached from the + on every surface, a leader's floor
  // included, so it is named before the floor check that would otherwise
  // answer with a floor tab.
  if (route.screen === 'raise') return '/raise';
  if (route.leaderView === 'floor') {
    return route.tab === 'home' ? '/floor' : `/floor/${route.tab}`;
  }
  if (route.screen === 'job' && route.woId != null) return `/job/${route.woId}`;
  if (route.screen === 'complete') return '/complete';
  if (route.screen === 'isw') return route.iswId ? `/isw/${iswIdToPath(route.iswId)}` : '/isw';
  return route.tab === 'home' ? '/' : `/${route.tab}`;
}

/**
 * Where a URL asked for actually lands, given who is asking.
 *
 * One correction, about an address that came from outside the app. For a
 * leader, an alert or a raise opens over the FLOOR rather than drilling them
 * into their last station: `parseRoute` cannot know who is asking and answers
 * 'station', but a leader tapping a push about a stopped line expects to find
 * the floor underneath it when they close the sheet — and the floor is where
 * their Back key goes anyway.
 */
export function landingRoute(asked: Route, isLeader: boolean): Route {
  if (isLeader && (asked.andonId != null || asked.screen === 'raise')) return { ...asked, leaderView: 'floor' };
  return asked;
}

/**
 * How far from home a route is: 0 for home itself, 1 for everywhere else.
 *
 * Deliberately flat. Back is meant to go home from wherever someone has
 * wandered to — a pod opened from a search, opened from the Pods tab, opened by
 * a scan — rather than retracing the taps that got them there, so screens
 * replace one another in the history stack instead of piling up on it.
 *
 * A leader's home is the floor, which makes the station they have drilled into
 * a step away from it: Back walks them off the station rather than out of the
 * app, which is the same promise the "Back to floor" control makes.
 *
 * The andon open over the Andon tab is NOT counted here, although it is in
 * the URL: it is still a sheet, and `useRouteSync` adds the sheet's entry the
 * same way it does for every other overlay. Counting it twice would leave an
 * entry Back could never spend.
 */
export function routeDepth(route: Route, isLeader: boolean): number {
  if (isLeader) return route.leaderView === 'floor' && route.tab === 'home' && route.screen !== 'raise' ? 0 : 1;
  return route.screen === 'home' && route.tab === 'home' ? 0 : 1;
}

/**
 * One instruction for the browser's history stack.
 *
 * `reset` lays a fresh home entry under everything — the cold-load case, where
 * the stack holds nothing of ours. `push` and `replace` write the current
 * screen's path; `back` spends entries we pushed earlier rather than replacing
 * on top of them, so the Back key keeps its one step home.
 */
export type HistoryStep =
  | { kind: 'reset' }
  | { kind: 'push' }
  | { kind: 'replace' }
  | { kind: 'back'; steps: number };

/**
 * How to get the history stack from the depth it is at to the depth it should
 * be at.
 *
 * @param have The depth of the entry showing, or null when the entry is not one
 * of ours: a first load, or a reload deep in the app.
 */
export function historyPlan(have: number | null, want: number): HistoryStep[] {
  if (have === null) {
    const steps: HistoryStep[] = [{ kind: 'reset' }];
    for (let d = 1; d <= want; d += 1) steps.push({ kind: 'push' });
    return steps;
  }
  if (want === have) return [{ kind: 'replace' }];
  if (want > have)
    return Array.from({ length: want - have }, () => ({ kind: 'push' }) as HistoryStep);
  return [{ kind: 'back', steps: have - want }];
}
